
import { useEffect, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface NewsItem {
  id?: string;
  title: string;
  description?: string;
  link: string;
  pubDate: string;
  source: string;
}

// Source configuration for both languages
export const sourcesConfig = {
  en: [
    'NCSC UK', 'NIST US', 'DarkReading', 'SANS Technology Institute',
    'TheHackerNews', 'Google Security Blog', 'SOPHOS', 'BleepingComputer'
  ],
  pl: [
    'Zaufana Trzecia Strona', 'Niebezpiecznik', 'Sekurak', 'Cyberdefence24',
    'Cybersecurity.org', 'AVLab', 'OpenSecurity', 'Kapitan Hack', 'CERT Orange',
    'Payload', 'CERT Polska'
  ]
};

// Items per page
export const ITEMS_PER_PAGE = 9;

export function useNewsFeed() {
  const { language, t } = useLanguage();
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Source filtering state
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [filteredNews, setFilteredNews] = useState<NewsItem[]>([]);

  // Initialize selected sources on language change
  useEffect(() => {
    let sources = sourcesConfig[language as keyof typeof sourcesConfig] || [];
    // If Polish, include English sources as well
    if (language === 'pl') {
      sources = [...sources, ...sourcesConfig.en];
    }
    setSelectedSources(sources);
    setCurrentPage(1);
  }, [language]);

  // Fetch news data from Supabase
  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      setError(null);

      try {
        let newsData: NewsItem[] = [];

        if (language === 'pl') {
          // Fetch both PL and EN news
          const [plData, enData] = await Promise.all([
            supabase.storage.from('dane').download('newspl.json'),
            supabase.storage.from('dane').download('newsen.json')
          ]);

          if (plData.error) throw new Error(`Error downloading PL news: ${plData.error.message}`);
          if (enData.error) throw new Error(`Error downloading EN news: ${enData.error.message}`);

          const plText = await plData.data.text();
          const enText = await enData.data.text();

          const plNews: NewsItem[] = JSON.parse(plText);
          const enNews: NewsItem[] = JSON.parse(enText);

          newsData = [...plNews, ...enNews];
        } else {
          // Fetch only EN news
          const { data, error: downloadError } = await supabase
            .storage
            .from('dane')
            .download('newsen.json');

          if (downloadError) {
            throw new Error(`Error downloading file: ${downloadError.message}`);
          }

          const jsonData = await data.text();
          newsData = JSON.parse(jsonData);
        }

        // Sort by publication date (newest first)
        const sortedNews = newsData.sort((a, b) =>
          new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime()
        );

        // Update the state with the fetched news
        setNews(sortedNews);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError('Failed to load news');
        setLoading(false);

        toast({
          title: "Error loading news",
          description: "Could not load the latest news. Please try again later.",
          variant: "destructive",
        });
      }
    };

    fetchNews();
  }, [language, toast]);

  // Apply source filters and pagination
  useEffect(() => {
    // Special handling for Cyberdefence24
    const filtered = news.filter(item => {
      if (selectedSources.includes('Cyberdefence24') && item.source.includes('Cyberdefence24')) {
        return true;
      }
      return selectedSources.includes(item.source) ||
        selectedSources.some(source => item.source.includes(source));
    });

    setFilteredNews(filtered);
    setTotalPages(Math.ceil(filtered.length / ITEMS_PER_PAGE));

    // Reset to first page if current page is out of bounds
    if (currentPage > Math.ceil(filtered.length / ITEMS_PER_PAGE)) {
      setCurrentPage(1);
    }
  }, [news, selectedSources, currentPage]);

  // Get current page data
  const getCurrentItems = () => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredNews.slice(startIndex, endIndex);
  };

  // Function to format date
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };

    try {
      return new Date(dateString).toLocaleDateString(
        language === 'en' ? 'en-US' : 'pl-PL',
        options
      );
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateString;
    }
  };

  // Toggle source filter
  const toggleSource = (source: string) => {
    if (source === 'ALL') {
      // Toggle all sources
      let allSources = sourcesConfig[language as keyof typeof sourcesConfig] || [];
      if (language === 'pl') {
        allSources = [...allSources, ...sourcesConfig.en];
      }

      if (selectedSources.length === allSources.length) {
        setSelectedSources([]); // Deselect all
      } else {
        setSelectedSources(allSources); // Select all
      }
    } else {
      if (selectedSources.includes(source)) {
        setSelectedSources(selectedSources.filter(s => s !== source));
      } else {
        setSelectedSources([...selectedSources, source]);
      }
    }
    setCurrentPage(1);
  };

  return {
    loading,
    error,
    currentPage,
    totalPages,
    selectedSources,
    getCurrentItems,
    formatDate,
    toggleSource,
    setCurrentPage,
    t
  };
}
